/**
 * Phase 5 media-coverage schema. Per CD spec § Phase-6-readiness note:
 *
 *   coverage: { level, count, byWindow, byType, sentiment, asymmetry }
 *   articles[]: per-source rows with credibility + sentiment + mapping confidence
 *
 * `asymmetry` is a first-class DERIVED field — Phase 6 (Inbox / Alert rules /
 * Briefing-export) layers on it without a schema redo. Compute once at fusion
 * time (or at coverage-ingest), persist on the outbreak record, then downstream
 * surfaces just read.
 */

/** Ordinal coverage volume: 0 silent · 1 low · 2 medium · 3 high. */
export type CoverageLevel = 0 | 1 | 2 | 3;

/** Time window for the coverage count. */
export type CoverageWindow = 'd7' | 'd30' | 'onset';

/** Source credibility tier (T1 official / T2 mainstream / T3 aggregator). */
export type CredibilityTier = 1 | 2 | 3;

/** Per-article sentiment — observed tone, NEVER a judgement on the outbreak. */
export type Sentiment = 'measured' | 'neutral' | 'alarmist';

/** Source-type taxonomy (matches the per-article `type` field). */
export type ArticleType = 'official' | 'mainstream' | 'aggregator';

/** Article→outbreak mapping confidence — degraded but never hidden when uncertain. */
export type MappingConfidence = 'high' | 'ambiguous';

/** One coverage snapshot per (outbreak × time-window). */
export interface CoverageWindowSnapshot {
  level: CoverageLevel;
  count: number;
}

/** Coverage-vs-severity asymmetry — descriptive flag, surfaced only when present. */
export interface Asymmetry {
  kind: 'under' | 'high';
  strength: 'mild' | 'strong';
}

/** Per-source-type article counts (for the inline 3-bar breakdown). */
export interface CoverageByType {
  agency: number;
  news: number;
  social: number;
}

/** Per-outbreak coverage record. Populated by the (future) media-ingest pipeline;
 *  for now provided by `coverage/sample-data.ts` for the design-system demo. */
export interface OutbreakCoverage {
  /** Stable outbreak id (matches the surveillance signal/cluster id). */
  outbreakId: string;
  /** Coverage volume per time window. */
  windows: Record<CoverageWindow, CoverageWindowSnapshot>;
  /** Source-type breakdown (cumulative for the default window). */
  byType: CoverageByType;
  /** Sentiment distribution across all articles. */
  sentiment: Record<Sentiment, number>;
  /** Coverage-vs-severity asymmetry (computed at fusion time). Null when
   *  neither under-reported nor high-profile (the system stays quiet). */
  asymmetry: Asymmetry | null;
  /** True when no media data has been ingested for this outbreak yet — the
   *  empty state shows "No coverage data ingested", NOT "Silent" (those are
   *  opposite findings; conflating them would be operationally dangerous). */
  noData?: boolean;
  /** Per-article confidence split for the header. */
  highConfidenceCount: number;
  ambiguousCount: number;
}

/** One article in the source drill-down pane. */
export interface CoverageArticle {
  id: string;
  outbreakId: string;
  headline: string;
  source: string;             // human-readable publisher name
  type: ArticleType;
  cred: CredibilityTier;
  date: string;               // ISO timestamp
  sentiment: Sentiment;
  conf: MappingConfidence;
  url?: string;               // optional click-through (omitted in synthetic data)
}

/** Coverage rating display metadata — keyed by CoverageLevel. */
export const COVERAGE_LEVEL_META: Record<CoverageLevel, { word: string; bars: number }> = {
  0: { word: 'Silent', bars: 0 },
  1: { word: 'Low', bars: 1 },
  2: { word: 'Medium', bars: 2 },
  3: { word: 'High', bars: 3 },
};

/** Credibility-tier display metadata. */
export const CREDIBILITY_META: Record<CredibilityTier, { word: string; short: string; cssVar: string }> = {
  1: { word: 'Official', short: 'T1', cssVar: 'var(--bm-cred-1)' },
  2: { word: 'Mainstream', short: 'T2', cssVar: 'var(--bm-cred-2)' },
  3: { word: 'Aggregator', short: 'T3', cssVar: 'var(--bm-cred-3)' },
};

/** Sentiment display metadata. */
export const SENTIMENT_META: Record<Sentiment, { word: string; icon: string; cssVar: string }> = {
  measured: { word: 'Measured', icon: '▭', cssVar: 'var(--bm-sent-measured)' },
  neutral: { word: 'Neutral', icon: '•', cssVar: 'var(--bm-sent-neutral)' },
  alarmist: { word: 'Alarmist', icon: '!', cssVar: 'var(--bm-sent-alarmist)' },
};

/** Compute asymmetry from a (windowed-coverage-level, expected-coverage-level)
 *  pair. Expected-coverage is currently derived from threat-tier; lifted into
 *  this pure fn so Phase 6 alert-rules can reuse the same logic. */
export function computeAsymmetry(
  observedLevel: CoverageLevel,
  expectedLevel: CoverageLevel,
): Asymmetry | null {
  const gap = observedLevel - expectedLevel;
  if (gap <= -2) return { kind: 'under', strength: 'strong' };
  if (gap === -1) return { kind: 'under', strength: 'mild' };
  if (gap >= 2) return { kind: 'high', strength: 'strong' };
  if (gap === 1) return { kind: 'high', strength: 'mild' };
  return null;
}
